import { FC, useCallback, useContext } from 'react';

import { layoutContext } from '@context/layout';
import { useRouter } from 'next/router';
import useLazyWorkspaceUrl from '@hooks/use-lazy-workspace-url';

import {
  PopupDescriptionContainer,
  PopupDescriptionContent,
  PopupDescriptionTopSection,
  PopupDescriptionTopSectionIcon,
  PopupDescriptionTopSectionText,
  PopupDescriptionTitle,
  PopupDescriptionText,
} from '../DescriptionBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard } from '@fortawesome/pro-solid-svg-icons';
import { faStars } from '@fortawesome/pro-light-svg-icons';

import { PopupButtonContainer, PopupButton, PopupButtonIcon, PopupButtonText } from '../EmulatedSideNavItems';
import { HighlightPopup } from '../HighlightPopup';
import { NavItems } from '@hooks/use-navigation-context';
import { analytics } from '@modules/analytics';
import { generateLinkComesFromEmailSignature } from '@components/EmailSignaturev2/OnboardingFlow/utils';
import { useHighlightPopupService } from '../HighlighPopupService';

const CardHighlightPopup: FC<React.PropsWithChildren<unknown>> = () => {
  const router = useRouter();
  const { state: navState } = useContext(layoutContext);
  const generateWorkspaceUrl = useLazyWorkspaceUrl();
  const cardsUrl = generateWorkspaceUrl(NavItems.Cards);
  const { closePopup } = useHighlightPopupService();

  const handleClickCreateCard = useCallback(() => {
    analytics.track({
      name: 'onboarding_signature_createcardprompt_clicked',
    });

    router.push(generateLinkComesFromEmailSignature(cardsUrl));
    closePopup();
  }, [cardsUrl, closePopup, router]);

  const handleClosePopup = useCallback(() => {
    analytics.track({
      name: 'onboarding_signature_createcardprompt_closed',
    });
    closePopup();
  }, [closePopup]);

  return (
    <HighlightPopup highlightId="card" delay={1000} onPopupClose={handleClosePopup}>
      <PopupButtonContainer>
        <PopupButton handleClick={handleClickCreateCard}>
          <PopupButtonIcon>
            <FontAwesomeIcon icon={faAddressCard} />
          </PopupButtonIcon>
          {!navState.collapsed && <PopupButtonText>Personal Cards</PopupButtonText>}
        </PopupButton>
      </PopupButtonContainer>
      <PopupDescriptionContainer>
        <PopupDescriptionContent>
          <PopupDescriptionTopSection>
            <PopupDescriptionTopSectionIcon>
              <FontAwesomeIcon icon={faStars} />
            </PopupDescriptionTopSectionIcon>
            <PopupDescriptionTopSectionText>Free</PopupDescriptionTopSectionText>
          </PopupDescriptionTopSection>
          <PopupDescriptionTitle>Create a digital business card</PopupDescriptionTitle>
          <PopupDescriptionText>
            Make your own fully customizable digital business card for free!
          </PopupDescriptionText>
        </PopupDescriptionContent>
      </PopupDescriptionContainer>
    </HighlightPopup>
  );
};

export { CardHighlightPopup };
